import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-12 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-article-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您要查看的文章不存在或已被删除</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '68影院',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12 md:pb-16 bg-background-50 min-h-screen">
        <article className="px-4 md:px-6 max-w-4xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '电影资讯', href: '/article/article-1' },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200 pt-6 mb-12">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 font-medium whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section>
              <h3 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关<span className="text-primary-500">文章</span>
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}